import { Link } from "react-router-dom";
import { Heart, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import PageWrapper from "@/components/layout/PageWrapper";
import Breadcrumb from "@/components/Breadcrumb";
import Icon from "@/components/Icon";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES, PILLARS } from "@/data/content";

const About = () => {
  const { t } = useLanguage();
  return (
    <PageWrapper
      title="About ADHD School Bhadrak — Aachary Dev Development Holistic School"
      description="Learn why ADHD School in Bhadrak is trusted by 150+ families for special education, therapy and holistic development."
    >
      <section className="adhd-container pt-10 lg:pt-14">
        <Breadcrumb pageKey="nav.about" />
        <p className="adhd-eyebrow mt-6">
          <Heart size={13} /> {t("brand.short")}
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-[color:var(--adhd-text)] max-w-3xl">
          {t("about.title")}
        </h1>
      </section>

      <section className="adhd-container mt-10 lg:mt-14 grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <p className="text-[17px] leading-relaxed text-[color:var(--adhd-muted)]">
            {t("about.body")}
          </p>
          <p className="mt-5 text-[17px] leading-relaxed text-[color:var(--adhd-muted)]">
            Every child is a universe in themselves — with their own pace of learning, their own joys, their own challenges. At ADHD School, we don't try to fit children into a fixed mould. Instead, we shape education, therapy, play and care around <em>them</em>. Our small batches, trained teachers and licensed therapists work together so that no child is left behind, and no parent feels alone.
          </p>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PILLARS.map((p) => (
              <div key={p.key} className="adhd-card p-4 flex flex-col gap-3" data-testid={`about-pillar-${p.key}`}>
                <span
                  className="grid place-items-center h-10 w-10 rounded-xl"
                  style={{ background: "rgba(58,124,165,0.10)" }}
                >
                  <Icon name={p.icon} size={18} className="text-[color:var(--adhd-primary)]" />
                </span>
                <p className="text-sm font-semibold leading-snug text-[color:var(--adhd-text)]">
                  {p.label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/services" className="adhd-btn-primary" data-testid="about-services">
              Explore Services <ArrowRight size={14} />
            </Link>
            <Link to="/principal" className="adhd-btn-secondary" data-testid="about-principal">
              {t("cta.principal")}
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-[color:var(--adhd-border)]">
            <img
              src="/about-trust.jpeg"
              alt="A child at ADHD School"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="mt-5 adhd-card p-6">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl" style={{ background: "rgba(90,147,103,0.16)" }}>
                <ShieldCheck size={18} style={{ color: "var(--adhd-success)" }} />
              </span>
              <p className="font-serif text-lg text-[color:var(--adhd-text)]">Our Promise</p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[color:var(--adhd-muted)]">
              A safe, caring environment where every child is seen, heard and supported — with patience, dignity and individualised attention.
            </p>
          </div>
        </div>
      </section>

      <section className="adhd-container mt-20 lg:mt-28 mb-10">
        <div className="adhd-card-soft p-10 sm:p-14 text-center">
          <Sparkles size={28} style={{ color: "var(--adhd-primary)" }} className="mx-auto" />
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-[color:var(--adhd-text)]">
            Come visit. See for yourself.
          </h2>
          <p className="mt-3 text-[color:var(--adhd-muted)] max-w-xl mx-auto">
            We warmly welcome parents for a campus tour. Meet our teachers, observe a therapy session and ask anything.
          </p>
          <Link to="/contact" className="mt-7 adhd-btn-primary inline-flex" data-testid="about-cta-visit">
            {t("cta.enquire")} <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PageWrapper>
  );
};

export default About;
